import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useApi, useClientOptions } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, FilterBar, DataTable, StatusBadge, ErrorBox } from "@/components/common/ui-bits";
import { fmtDate, money, optionList, PAYMENT_METHODS, PAYMENT_STATUSES } from "@/lib/format";

function LawPaySync({ onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/lawpay/sync");
      if (!r.data.configured) toast.error("LawPay no está conectado");
      else if (r.data.registered) { toast.success(`${r.data.registered} pago(s) nuevo(s) de LawPay registrado(s)`); onDone(); }
      else toast.info("No hay pagos nuevos en LawPay");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return <Button variant="outline" onClick={run} disabled={busy} data-testid="lawpay-sync-button">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}Revisar LawPay ahora</Button>;
}

export default function Payments() {
  const { user } = useAuth();
  const portal = user.role === "client";
  const [f, setF] = useState({});
  const { data, loading, error, reload } = useApi("/payments", f);
  const clients = useClientOptions();
  const navigate = useNavigate();
  const counted = (data || []).filter((p) => ["Pagado", "Parcial"].includes(p.status));
  return (
    <div>
      <PageHeader title={portal ? "Mis pagos" : "Pagos"} subtitle={`Cobrado: ${money(counted.reduce((s, p) => s + p.amount, 0))} · ${counted.length} pago(s). Los pagos se registran desde cada factura.`}
        actions={user.role === "admin" ? <LawPaySync onDone={reload} /> : null} />
      <FilterBar values={f} onChange={setF} testid="payments-filters" filters={[
        { key: "method", label: "Método", options: optionList(PAYMENT_METHODS) }, { key: "status", label: "Estado", options: optionList(PAYMENT_STATUSES) },
        ...(portal ? [] : [{ key: "client_id", label: "Cliente", options: clients }])]} />
      <ErrorBox text={error} />
      <DataTable testid="payments-table" loading={loading} rows={data} empty="No hay pagos." onRowClick={(r) => navigate(`${portal ? "/portal/facturas" : "/facturas"}/${r.invoice_id}`)}
        columns={[{ key: "date", label: "Fecha", render: (r) => fmtDate(r.date) }, ...(portal ? [] : [{ key: "client_name", label: "Cliente" }]),
          { key: "invoice_number", label: "Factura", render: (r) => <span className="num text-xs">{r.invoice_number}</span> },
          { key: "amount", label: "Importe", render: (r) => <span className="num">{money(r.amount)}</span> }, { key: "method", label: "Método" },
          { key: "reference", label: "Referencia" }, ...(portal ? [] : [{ key: "registered_by_name", label: "Registró" }]),
          { key: "status", label: "Estado", render: (r) => <StatusBadge status={r.status} /> }]} />
    </div>
  );
}
